import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { appConfig } from '@/config/app.ts'
import { RowsPerPageVale } from '@/core/types/AppConfig.ts'

type Pagination = {
  current?: number
  pageSize?: number
}

export type paginationProps = {
  currentFilters: string
  filterSetter: (filters: string) => void
  offset: number
  total: number
  initialPage?: number
  pageBased?: boolean
  pageSize?: number
  pageSizeOptions?: number[]
}

const usePagination = ({
  currentFilters,
  filterSetter,
  offset,
  total,
  initialPage = 1,
  pageBased = false,
  pageSize,
  pageSizeOptions
}: paginationProps) => {
  const [dataTable, setDataTable] = useState({
    current: 0,
    pageSize: pageSize || appConfig.pagination.defaultValue,
    total: 0
  })

  const filterConf = useMemo(
    () => ({
      pageFilterName: 'page',
      rowsPerPageFilterName: 'limit',
      pageSizeOptions:
        pageSizeOptions || appConfig.pagination.rowsPerPageOptions
    }),
    [pageSizeOptions]
  )

  useEffect(() => {
    const LIMIT = dataTable.pageSize
    setDataTable({
      current: pageBased ? offset : Math.floor(offset / LIMIT),
      pageSize: LIMIT,
      total: total
    })
  }, [dataTable.pageSize, offset, pageBased, total])

  const onPaginateFilters = useCallback(
    (pagination: Pagination) => {
      const urlParams = new URLSearchParams(currentFilters)
      if (pagination.current !== null && pagination.current !== undefined)
        urlParams.set(filterConf.pageFilterName, String(pagination.current))
      if (pagination.pageSize !== null && pagination.pageSize !== undefined)
        urlParams.set(
          filterConf.rowsPerPageFilterName,
          String(pagination.pageSize)
        )
      const newURLString = `?${urlParams.toString()}`
      filterSetter(newURLString)
    },
    [
      currentFilters,
      filterConf.pageFilterName,
      filterConf.rowsPerPageFilterName,
      filterSetter
    ]
  )

  const handleChangePage = useCallback(
    (_: unknown, newPage: number) => {
      onPaginateFilters({ current: pageBased ? newPage : newPage + 1 })
      setDataTable({
        ...dataTable,
        current: pageBased ? newPage : newPage + 1
      })
    },
    [dataTable, onPaginateFilters, pageBased]
  )

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const pageSize = parseInt(event.target.value, 10) as RowsPerPageVale
      onPaginateFilters({ current: initialPage, pageSize })
      setDataTable({
        ...dataTable,
        current: 0,
        pageSize
      })
    },
    [dataTable, initialPage, onPaginateFilters]
  )

  return {
    pagination: {
      handleChangePage,
      handleChangeRowsPerPage,
      paginationConf: {
        page: dataTable.current,
        rowsPerPage: dataTable.pageSize as number,
        count: dataTable.total,
        rowsPerPageOptions: filterConf.pageSizeOptions as unknown as number[]
      }
    }
  }
}

export default usePagination
